import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const AuthCtx = createContext(null);

// NOC/CCTV kiosk pairing: a TV can open /noc?screen=<token> (or /noc/cameras?screen=…) to sign
// in as the read-only "screen" account WITHOUT the Turnstile login form. We consume the param at
// module load — before the provider reads localStorage — then scrub it from the URL.
(function consumeScreenToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const screen = params.get('screen');
    if (screen) {
      localStorage.setItem('ptt_token', screen);
      localStorage.removeItem('ptt_user');
      params.delete('screen');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash);
    }
  } catch { /* noop */ }
})();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ptt_user') || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('ptt_token'));
  const [loading, setLoading] = useState(!!token);

  const persist = (t, u) => {
    localStorage.setItem('ptt_token', t);
    localStorage.setItem('ptt_user', JSON.stringify(u));
    setToken(t); setUser(u);
  };

  const login = async (email, password, turnstile_token) => {
    const { data } = await api.post('/auth/login', { email, password, turnstile_token });
    persist(data.access_token, data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    return data; // { ok, email_verification_required, message } — no token until verified
  };

  const verifyEmail = async (token) => {
    const { data } = await api.post('/auth/verify-email', { token });
    persist(data.access_token, data.user);
    return data.user;
  };

  const resendVerification = async (email, turnstile_token) => {
    const { data } = await api.post('/auth/resend-verification', { email, turnstile_token });
    return data;
  };

  const logout = () => {
    localStorage.removeItem('ptt_token');
    localStorage.removeItem('ptt_user');
    setToken(null); setUser(null);
  };

  const refreshMe = useCallback(async () => {
    if (!localStorage.getItem('ptt_token')) return null;
    try {
      const { data } = await api.get('/auth/me');
      localStorage.setItem('ptt_user', JSON.stringify(data));
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (token) {
      refreshMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, refreshMe]);

  return (
    <AuthCtx.Provider value={{ user, token, login, register, verifyEmail, resendVerification, logout, refreshMe, setUser, loading }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
